import { MultiSelectControl, Pill } from '@sequencehq/core-components'
import { FC, useCallback, useMemo } from 'react'
import { useFetchCustomerQuery, useUpdateCustomer } from 'modules/Customer'
import { getPrimaryBillingContact } from 'CustomerContacts/utils/getPrimaryBillingContact'
import {
  assignContactPurpose,
  setBillingNotifications,
  withoutPurpose
} from 'CustomerContacts/utils/contactPurposes'
import partition from 'lodash/partition'
import { toContactBody } from 'CustomerContacts/utils/toContactBody'
import { useBillingContacts } from 'CustomerContacts/hooks/useBillingContacts'

type Props = {
  customerId: string
  onChange?: () => void
  disabled?: boolean
  'data-testid'?: string
}

export const CustomerBillingContactsSelect: FC<Props> = ({
  customerId,
  onChange,
  disabled = false,
  'data-testid': dataTestid
}) => {
  const { customerDetails: customer } = useFetchCustomerQuery(customerId)
  const billingContacts = useBillingContacts(customerId)
  const updateBillingContacts = useUpdateBillingContacts(customerId)

  const options = useMemo(() => {
    return (customer?.contacts ?? []).map(contact => ({
      label: contact.email,
      value: contact.email
    }))
  }, [customer?.contacts])

  return (
    <MultiSelectControl
      options={options}
      initialValues={billingContacts.map(c => c.email ?? '')}
      trigger={
        <CustomerBillingContactsPill
          customerId={customerId}
          data-testid={dataTestid}
        />
      }
      onChange={customerEmails => {
        updateBillingContacts(customerEmails)
        onChange?.()
      }}
      disabled={disabled}
      data-testid={dataTestid ? `${dataTestid}.dropdown` : undefined}
    />
  )
}

export const CustomerBillingContactsPill: FC<{
  customerId: string
  'data-testid'?: string
}> = ({ customerId, 'data-testid': dataTestId }) => {
  const billingContacts = useBillingContacts(customerId)

  return (
    <Pill
      more={billingContacts.length > 1 ? billingContacts.length - 1 : undefined}
      data-testid={dataTestId}
    >
      {billingContacts[0]?.email ?? 'No billing contacts'}
    </Pill>
  )
}

function useUpdateBillingContacts(customerId: string) {
  const { customerDetails: customer } = useFetchCustomerQuery(customerId)
  const allContacts = customer?.contacts ?? []
  const { updateCustomer } = useUpdateCustomer(customerId)

  return useCallback(
    (customerEmails: string[]) => {
      const [billingContacts, nonBillingContacts] = partition(allContacts, c =>
        customerEmails.includes(c.email)
      )

      const subscribedContacts = billingContacts.map(c =>
        setBillingNotifications(c, true)
      )

      const firstBillingContact = subscribedContacts.at(0)
      const needsPrimary =
        firstBillingContact && !getPrimaryBillingContact(subscribedContacts)

      const newBillingContacts = needsPrimary
        ? assignContactPurpose(
            subscribedContacts,
            firstBillingContact.id,
            'BILLING'
          )
        : subscribedContacts

      const newContacts = [
        ...newBillingContacts,
        ...nonBillingContacts.map(c =>
          setBillingNotifications(withoutPurpose(c, 'BILLING'), false)
        )
      ]

      updateCustomer({
        contacts: newContacts.map(toContactBody)
      })
    },
    [updateCustomer, allContacts]
  )
}
